import type { BannerResponse, ShipstarConfig } from "./types";
import { escapeAttr, escapeHtml, debugLog } from "./utils";

const DISMISS_KEY_PREFIX = "shipstar-banner-dismissed:";

const shipstarIcon = `<svg width="12" height="12" viewBox="0 0 195 195" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M123.215 24.3304C125.235 21.8047 129.145 21.8047 128.921 24.3304L124.507 73.9972C124.406 75.1267 125.218 75.8915 126.518 75.8915H183.666C186.572 75.8915 186.27 79.2898 183.225 80.8508L123.349 111.547C121.987 112.245 120.997 113.482 120.897 114.612L116.482 164.278C116.258 166.804 112.161 168.904 110.504 167.343L77.9123 136.648C77.1711 135.949 75.7473 135.949 74.3856 136.648L14.5092 167.343C11.4643 168.904 9.23429 166.804 11.2549 164.278L50.9888 114.612C51.8924 113.482 52.0024 112.245 51.2612 111.547L18.6698 80.8508C17.0125 79.2898 19.7312 75.8915 22.6373 75.8915H79.7856C81.0852 75.8915 82.577 75.1267 83.4806 73.9972L123.215 24.3304Z" fill="currentColor"/></svg>`;

/** Only http(s) links are emitted; anything else renders as no link. */
export function safeHref(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;
  return /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

/**
 * A banner is identified by its slug plus publication time: dismissing one
 * hides it until a *new* banner is published under the same key.
 */
function dismissValue(data: BannerResponse): string {
  return data.published_at || data.headline;
}

export function isBannerDismissed(data: BannerResponse): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY_PREFIX + data.slug) === dismissValue(data);
  } catch {
    return false;
  }
}

export function dismissBanner(data: BannerResponse): void {
  try {
    localStorage.setItem(DISMISS_KEY_PREFIX + data.slug, dismissValue(data));
  } catch {
    // Storage unavailable (private mode, blocked) — the banner simply reappears next load
  }
}

export function renderBannerWidget(data: BannerResponse, config: ShipstarConfig): string {
  const href = safeHref(data.link_url);
  // The API decides the badge (default "New", re-worded or switched off per
  // banner); the widget only renders what it is given
  const badge = typeof data.badge === "string" ? data.badge.trim() : "";
  const badgeHtml = badge ? `<span class="ss-banner-badge">${escapeHtml(badge)}</span>` : "";
  const bodyHtml = data.body
    ? `<span class="ss-banner-body">${escapeHtml(data.body)}</span>`
    : "";
  const ctaHtml = href
    ? `<a class="ss-banner-cta" href="${escapeAttr(href)}" target="_top" rel="noopener">${escapeHtml(data.cta_label || "Learn more")}</a>`
    : "";
  const brandHtml = config.branding
    ? `<a class="ss-banner-brand" href="https://shipstar.ai" target="_blank" rel="noopener noreferrer" aria-label="Powered by Shipstar" title="Powered by Shipstar">${shipstarIcon}</a>`
    : "";
  const dismissHtml = config.dismissible
    ? `<button type="button" class="ss-banner-dismiss" aria-label="Dismiss announcement">&#215;</button>`
    : "";

  return `<div class="ss-banner" role="region" aria-label="Announcement">
    <div class="ss-banner-text">
      ${badgeHtml}
      <span class="ss-banner-headline">${escapeHtml(data.headline)}</span>
      ${bodyHtml}
    </div>
    ${ctaHtml}
    ${brandHtml}
    ${dismissHtml}
  </div>`;
}

export function mountBannerInteractivity(
  shadowRoot: ShadowRoot,
  data: BannerResponse,
  config: ShipstarConfig
): void {
  const button = shadowRoot.querySelector<HTMLButtonElement>(".ss-banner-dismiss");
  if (!button) return;
  button.addEventListener("click", () => {
    dismissBanner(data);
    const bar = shadowRoot.querySelector(".ss-banner");
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
    debugLog(config, "Banner dismissed.");
  });
}
